import React, { useContext, useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import { getAvailableIntegrations } from "../../services/RepositoryService";
import IntegrationsViewToggle from "./IntegrationsViewToggle";
import "../../starek.css";

const GET_INTEGRATIONS_FOR_USAGE = gql`
    query GetIntegrationsForUsage($plantId: Int!) {
        integrations(where: { plant_id: { equals: $plantId } }) {
            id
            integration_key
        }
    }
`;

const ALL_CATEGORIES = "All";

const IntegrationsAvailable = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const [templates, setTemplates] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);

    useEffect(() => {
        getAvailableIntegrations().then((data) => setTemplates(data || []));
    }, []);

    const { data } = useQuery(GET_INTEGRATIONS_FOR_USAGE, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
        fetchPolicy: "network-only",
    });

    const usageByKey = useMemo(() => {
        const counts = {};
        for (const integration of data?.integrations ?? []) {
            counts[integration.integration_key] = (counts[integration.integration_key] || 0) + 1;
        }
        return counts;
    }, [data]);

    const categories = useMemo(() => {
        const set = new Set((templates || []).map((t) => t.category).filter(Boolean));
        return [ALL_CATEGORIES, ...Array.from(set).sort()];
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return (templates || []).filter((t) => {
            const matchesCategory = activeCategory === ALL_CATEGORIES || t.category === activeCategory;
            const matchesSearch =
                !q ||
                t.name?.toLowerCase().includes(q) ||
                t.description?.toLowerCase().includes(q) ||
                t.vendor?.toLowerCase().includes(q);
            return matchesCategory && matchesSearch;
        });
    }, [templates, searchText, activeCategory]);

    const openInstall = (templateKey) => {
        navigate(`/integrations/0?template=${templateKey}`);
    };

    if (templates === null) return <EntityLoadingState icon="fa-th" />;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Available Integrations</div>

            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <input
                    type="search"
                    className="form-control w-40 min-width-240px"
                    placeholder="Search for an integration…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                <IntegrationsViewToggle current="available" />
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap">
                {categories.map((category) => (
                    <button
                        key={category}
                        className={`btn btn-sm ${
                            activeCategory === category ? "btn-primary" : "btn-outline-secondary"
                        }`}
                        onClick={() => setActiveCategory(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {filteredTemplates.length === 0 ? (
                <div className="text-muted text-center py-5">
                    No integrations match your search.
                </div>
            ) : (
                <div className="row g-3">
                    {filteredTemplates.map((template) => {
                        const installedCount = usageByKey[template.key] || 0;

                        return (
                            <div key={template.key} className="col-lg-3 col-md-4 col-sm-6">
                                <div className="card h-100 shadow-sm">
                                    <div className="card-body d-flex flex-column">
                                        <div className="d-flex align-items-start justify-content-between mb-2">
                                            {template.icon ? (
                                                <img
                                                    src={template.icon}
                                                    alt=""
                                                    className="w-36px h-36px object-fit-contain"
                                                    onError={(e) => { e.target.style.display = "none"; }}
                                                />
                                            ) : (
                                                <i className="fa fa-plug fa-2x text-primary" />
                                            )}
                                            {installedCount > 0 && (
                                                <span className="badge bg-success">
                                                    In use ({installedCount})
                                                </span>
                                            )}
                                        </div>
                                        <h6 className="mb-1">{template.name}</h6>
                                        {template.category && (
                                            <span className="badge bg-light text-dark border mb-2 align-self-start">
                                                {template.category}
                                            </span>
                                        )}
                                        <p className="text-muted mb-3 flex-grow-1 font-size-14px">
                                            {template.description}
                                        </p>
                                        <div className="d-flex align-items-center justify-content-between">
                                            {template.docs_url ? (
                                                <a
                                                    href={template.docs_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="small"
                                                >
                                                    Learn more
                                                </a>
                                            ) : (
                                                <span />
                                            )}
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => openInstall(template.key)}
                                            >
                                                {installedCount > 0 && template.allow_multiple_instances
                                                    ? "Add Another"
                                                    : "Add Integration"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export { IntegrationsAvailable };
